import './App.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const META_APP_ID = process.env.REACT_APP_META_APP_ID || '';
const META_CONFIG_ID = process.env.REACT_APP_META_CONFIG_ID || '';
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';
const ONBOARDING_API_URL = (process.env.REACT_APP_ONBOARDING_API_URL || '').trim();
const MESSAGING_API_URL = (process.env.REACT_APP_MESSAGING_API_URL || '').trim();
const MESSAGING_API_KEY = (process.env.REACT_APP_MESSAGING_API_KEY || '').trim();
const META_SDK_VERSION = 'v21.0';

const CALLBACK_PATHS = ['/auth/facebook/callback', '/oauth/callback'];
const PRIVACY_POLICY_PATH = '/privacy-policy';
const DATA_DELETION_PATH = '/data-deletion';
const REVIEW_MESSAGES_PATH = '/review/messages';
export const ONBOARDING_SESSION_STORAGE_KEY = 'wa_coex_onboarding_session';

const FLOW_STATUS = {
  idle: 'idle',
  sdkNotReady: 'sdk not ready',
  openingPopup: 'opening popup',
  receivedCode: 'received code',
  sendingToBackend: 'sending to backend',
  success: 'onboarding received successfully',
  backendError: 'backend error',
  cancelled: 'cancelled / not authorized',
  unexpected: 'unexpected response',
};

export const isOnboardingSessionExpired = (session, now = Date.now()) => {
  const expiration = Date.parse(session?.expires_at);
  return !Number.isFinite(expiration) || expiration <= now;
};

export const isValidOnboardingSession = (session, now = Date.now()) => Boolean(
  session
  && typeof session.session_id === 'string'
  && session.session_id.startsWith('wos_')
  && typeof session.state === 'string'
  && session.state.trim()
  && typeof session.expires_at === 'string'
  && typeof session.client_name === 'string'
  && session.client_name.trim()
  && !isOnboardingSessionExpired(session, now)
);

export const removeOnboardingSession = () => {
  window.sessionStorage.removeItem(ONBOARDING_SESSION_STORAGE_KEY);
};

export const readOnboardingSession = () => {
  try {
    const stored = window.sessionStorage.getItem(ONBOARDING_SESSION_STORAGE_KEY);
    if (!stored) return null;
    const session = JSON.parse(stored);
    if (isValidOnboardingSession(session)) return session;
  } catch (error) {
    // Corrupt browser storage is treated like a missing session.
  }
  removeOnboardingSession();
  return null;
};

export const saveOnboardingSession = (session) => {
  if (!isValidOnboardingSession(session)) return false;
  try {
    window.sessionStorage.setItem(ONBOARDING_SESSION_STORAGE_KEY, JSON.stringify(session));
    return true;
  } catch (error) {
    return false;
  }
};

const removeInviteFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('invite');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

export const postCodeToOnboarding = async ({
  code, session, setLoginStatus, setResultText, onSessionConsumed,
}) => {
  if (!ONBOARDING_API_URL) {
    setLoginStatus(FLOW_STATUS.backendError);
    setResultText('No fue posible registrar la conexión por una configuración incompleta del portal.');
    return;
  }

  if (typeof code !== 'string' || !code.trim() || !isValidOnboardingSession(session)) {
    setLoginStatus(FLOW_STATUS.backendError);
    setResultText('La sesión de conexión no es válida o expiró. Solicita una nueva invitación.');
    return;
  }

  setLoginStatus(FLOW_STATUS.sendingToBackend);
  setResultText('Registrando la conexión de forma segura...');

  const payload = {
    session_id: session.session_id,
    state: session.state,
    code,
  };

  try {
    const response = await fetch(`${ONBOARDING_API_URL}/api/onboarding/session/code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      mode: 'cors',
      redirect: 'error',
    });

    if (response.status === 201) {
      let body;
      try {
        body = await response.json();
      } catch (error) {
        onSessionConsumed();
        setLoginStatus(FLOW_STATUS.backendError);
        setResultText('La conexión pudo haber sido recibida, pero no fue posible confirmar el resultado. Contacta a soporte.');
        return;
      }
      onSessionConsumed();
      if (body?.ok === true && body?.onboarding?.status === 'CODE_RECEIVED') {
        setLoginStatus(FLOW_STATUS.success);
        setResultText('La conexión fue recibida correctamente. Puedes cerrar esta ventana.');
      } else {
        setLoginStatus(FLOW_STATUS.backendError);
        setResultText('La conexión pudo haber sido recibida, pero no fue posible confirmar el resultado. Contacta a soporte.');
      }
      return;
    }

    if (response.status === 400) {
      onSessionConsumed();
      setLoginStatus(FLOW_STATUS.backendError);
      setResultText('La sesión de conexión ya no es válida o ya fue utilizada. Solicita una nueva invitación.');
      return;
    }
    setLoginStatus(FLOW_STATUS.backendError);
    if (response.status === 403) {
      setResultText('No fue posible validar la conexión desde este portal. Contacta a soporte.');
    } else if (response.status === 405) {
      setResultText('No fue posible registrar la conexión por una configuración del servicio. Contacta a soporte.');
    } else {
      setResultText('No fue posible registrar la conexión. Inténtalo nuevamente o contacta a soporte.');
    }
  } catch (error) {
    setLoginStatus(FLOW_STATUS.backendError);
    setResultText('No fue posible confirmar la conexión con el servicio. Inténtalo nuevamente.');
  }
};

function CallbackView() {
  const [loginStatus, setLoginStatus] = useState(FLOW_STATUS.idle);
  const [resultText, setResultText] = useState('Waiting for callback information...');
  const wasCodeProcessed = useRef(false);
  const submissionLock = useRef(false);

  const callbackData = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const captured = {
      path: window.location.pathname,
      code: params.get('code') || '',
      error: params.get('error') || '',
      errorDescription: params.get('error_description') || '',
    };
    ['code', 'state', 'error', 'error_description'].forEach((name) => params.delete(name));
    const search = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`);
    return captured;
  }, []);

  useEffect(() => {
    if (callbackData.error) {
      setLoginStatus(FLOW_STATUS.cancelled);
      setResultText('Meta no completó la autorización. Puedes volver a intentarlo mientras la sesión siga vigente.');
      return;
    }

    if (callbackData.code && !wasCodeProcessed.current) {
      wasCodeProcessed.current = true;
      const session = readOnboardingSession();
      if (!session) {
        setLoginStatus(FLOW_STATUS.backendError);
        setResultText('La sesión de conexión no es válida o expiró. Solicita una nueva invitación.');
        return;
      }
      submissionLock.current = true;
      const metaCode = callbackData.code;
      callbackData.code = '';
      void postCodeToOnboarding({
        code: metaCode,
        session,
        setLoginStatus,
        setResultText,
        onSessionConsumed: removeOnboardingSession,
      }).finally(() => { submissionLock.current = false; });
      return;
    }

    setLoginStatus(FLOW_STATUS.unexpected);
    setResultText('No useful information was received in the callback.');
  }, [callbackData]);

  return (
    <main className="portal-shell">
      <section className="card">
        <h1>Meta OAuth Callback</h1>
        <p><strong>Route:</strong> {callbackData.path}</p>
        <p><strong>Status:</strong> {loginStatus}</p>

        <div className="result-box">
          <h2>Result</h2>
          <pre>{resultText}</pre>
        </div>

      </section>
    </main>
  );
}

function PrivacyPolicyView() {
  return (
    <main className="portal-shell">
      <section className="card legal-card">
        <h1>Privacy Policy</h1>
        <p>
          <strong>Service provider:</strong> AmazingMacros. This portal is used to initiate and support WhatsApp
          Business coexistence onboarding with Meta for authorized clients.
        </p>

        <h2>Data We May Process</h2>
        <ul>
          <li>Contact and business identification data you share during onboarding.</li>
          <li>Technical connection process data (for example, integration status and authorization codes).</li>
          <li>Minimum operational logs for support, security, and service traceability.</li>
        </ul>

        <h2>Purpose of Processing</h2>
        <ul>
          <li>Enable and support WhatsApp coexistence onboarding.</li>
          <li>Manage Meta/WhatsApp integrations when authorized by the client.</li>
          <li>Handle technical incidents and requests related to portal operations.</li>
        </ul>

        <h2>Meta/WhatsApp Integration</h2>
        <p>
          The service may integrate with Meta products to complete WhatsApp Business onboarding. When this occurs,
          data processing is also subject to the applicable policies and terms of those platforms.
        </p>

        <h2>Use and Protection of Data</h2>
        <ul>
          <li>We do not sell personal data.</li>
          <li>We apply reasonable measures to protect information processed through the portal.</li>
          <li>We retain data only as long as necessary for the service purpose.</li>
        </ul>

        <h2>Contact and Support</h2>
        <p>
          If you need assistance or would like to submit a privacy inquiry, contact us at:{' '}
          <a href="mailto:sentryx@amazingmacros.com">sentryx@amazingmacros.com</a>
        </p>
        <p>
          To request data deletion, see:{' '}
          <a href={DATA_DELETION_PATH}>Data Deletion</a>.
        </p>

        <p className="legal-meta"><strong>Last updated:</strong> March 29, 2026</p>
      </section>
    </main>
  );
}

function DataDeletionView() {
  return (
    <main className="portal-shell">
      <section className="card legal-card">
        <h1>Data Deletion Request</h1>
        <p>
          <strong>Service provider:</strong> AmazingMacros. If you want to request deletion of your data associated
          with the WhatsApp Business coexistence onboarding portal with Meta, you can do so by email.
        </p>

        <h2>How to Request Deletion</h2>
        <ol>
          <li>Send an email to <a href="mailto:sentryx@amazingmacros.com">sentryx@amazingmacros.com</a>.</li>
          <li>Use this email subject: <strong>Data Deletion Request</strong>.</li>
          <li>Include the information required to identify your case.</li>
        </ol>

        <h2>Recommended Information to Identify Your Request</h2>
        <ul>
          <li>Full name and company (if applicable).</li>
          <li>Email used during onboarding or integration.</li>
          <li>Related WhatsApp phone number (if applicable).</li>
          <li>Brief request description.</li>
        </ul>

        <h2>Handling Process</h2>
        <p>
          Each request will be reviewed and handled as appropriate, considering identity verification, operational
          obligations, and applicable legal requirements.
        </p>

        <h2>Temporary Contact Channel</h2>
        <p>
          Support email: <a href="mailto:sentryx@amazingmacros.com">sentryx@amazingmacros.com</a>
        </p>
        <p>
          More information about data processing:{' '}
          <a href={PRIVACY_POLICY_PATH}>Privacy Policy</a>.
        </p>

        <p className="legal-meta"><strong>Last updated:</strong> March 29, 2026</p>
      </section>
    </main>
  );
}

function NoInvitationView() {
  return (
    <main className="no-invitation-view">
      Disculpe, pero usted no posee una invitación
    </main>
  );
}

function InvitationValidationPendingView() {
  return <main className="no-invitation-view">Validando invitación...</main>;
}

function InvitationValidationErrorView() {
  return <main className="no-invitation-view">No fue posible validar la invitación. Inténtelo nuevamente.</main>;
}

function OnboardingGate() {
  const invite = useMemo(
    () => (new URLSearchParams(window.location.search).get('invite') || '').trim(),
    []
  );
  const [onboardingSession] = useState(() => readOnboardingSession());
  const [validation, setValidation] = useState(() => {
    if (onboardingSession) return 'session';
    if (!invite) return 'missing';
    return ONBOARDING_API_URL ? 'pending' : 'error';
  });

  useEffect(() => {
    if (validation !== 'pending') return undefined;

    const controller = new AbortController();
    const validateInvitation = async () => {
      try {
        const response = await fetch(`${ONBOARDING_API_URL}/api/onboarding/invitations/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invite }),
          credentials: 'omit',
          cache: 'no-store',
          referrerPolicy: 'no-referrer',
          mode: 'cors',
          redirect: 'error',
          signal: controller.signal,
        });
        if (response.status === 400) {
          removeInviteFromUrl();
          setValidation('invalid');
          return;
        }
        if (response.status !== 200) {
          setValidation('error');
          return;
        }
        const payload = await response.json();
        setValidation(payload?.ok === true && payload?.valid === true ? 'valid' : 'error');
      } catch (error) {
        if (error?.name !== 'AbortError') setValidation('error');
      }
    };
    void validateInvitation();
    return () => controller.abort();
  }, [invite, validation]);

  if (validation === 'session') return <HomeView initialInvite="" initialSession={onboardingSession} />;
  if (validation === 'valid') return <HomeView initialInvite={invite} initialSession={null} />;
  if (validation === 'pending') return <InvitationValidationPendingView />;
  if (validation === 'error') return <InvitationValidationErrorView />;
  return <NoInvitationView />;
}

function HomeView({ initialInvite, initialSession }) {
  const [sdkStatus, setSdkStatus] = useState('Loading Meta SDK...');
  const [sdkReady, setSdkReady] = useState(false);
  const [loginStatus, setLoginStatus] = useState(FLOW_STATUS.idle);
  const [resultText, setResultText] = useState('Waiting for action.');
  const [invite, setInvite] = useState(initialInvite);
  const [onboardingSession, setOnboardingSession] = useState(initialSession);
  const [invitationRejected, setInvitationRejected] = useState(false);
  const [operation, setOperation] = useState('idle');
  const operationLock = useRef(false);
  const codeSubmissionLock = useRef(false);
  const sessionCompleted = useRef(false);

  const missingConfig = useMemo(() => {
    const missing = [];
    if (!META_APP_ID) missing.push('REACT_APP_META_APP_ID');
    if (!META_CONFIG_ID) missing.push('REACT_APP_META_CONFIG_ID');
    if (!META_PIXEL_ID) missing.push('REACT_APP_META_PIXEL_ID');
    return missing;
  }, []);

  const hasBackendConfig = Boolean(ONBOARDING_API_URL);

  const consumeLocalSession = () => {
    sessionCompleted.current = true;
    removeOnboardingSession();
    setOnboardingSession(null);
  };

  useEffect(() => {
    if (initialSession) {
      setResultText('Sesión activa. Puedes continuar la conexión con Meta.');
    }
  }, [initialSession]);

  useEffect(() => {
    if (missingConfig.length > 0) {
      setSdkStatus(`Missing variables: ${missingConfig.join(', ')}`);
      return;
    }

    if (window.fbq) {
      window.fbq('init', META_PIXEL_ID);
      window.fbq('track', 'PageView');
    } else {
      /* eslint-disable */
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      window.fbq('init', META_PIXEL_ID);
      window.fbq('track', 'PageView');
      /* eslint-enable */
    }

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: META_APP_ID,
        cookie: true,
        xfbml: false,
        version: META_SDK_VERSION,
      });

      setSdkReady(true);
      setSdkStatus('SDK ready');
    };

    const scriptId = 'facebook-jssdk';
    if (document.getElementById(scriptId)) {
      if (window.FB) {
        setSdkReady(true);
        setSdkStatus('SDK ready');
      }
      return;
    }

    const js = document.createElement('script');
    js.id = scriptId;
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    js.async = true;
    js.onerror = () => {
      setSdkStatus('Error loading SDK');
      setLoginStatus(FLOW_STATUS.sdkNotReady);
      setResultText('Could not load the Meta SDK.');
    };

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) firstScript.parentNode.insertBefore(js, firstScript);
    else document.head.appendChild(js);
  }, [missingConfig]);

  const getOrCreateOnboardingSession = async () => {
    if (isValidOnboardingSession(onboardingSession)) return onboardingSession;
    if (onboardingSession) {
      removeOnboardingSession();
      setOnboardingSession(null);
      setResultText('La sesión expiró. Solicita una nueva invitación.');
      return null;
    }
    if (!invite) return null;

    setOperation('consuming');
    setResultText('Validando invitación...');
    try {
      const response = await fetch(`${ONBOARDING_API_URL}/api/onboarding/invitations/consume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invite }),
      });
      if (!response.ok) {
        if (response.status === 400) {
          setInvite('');
          removeInviteFromUrl();
          setInvitationRejected(true);
        } else if (response.status === 403) {
          setResultText('No fue posible validar la invitación desde este portal.');
        } else {
          setResultText('No fue posible iniciar la conexión. Inténtalo nuevamente o contacta a soporte.');
        }
        return null;
      }
      let payload;
      try {
        payload = await response.json();
      } catch (error) {
        setResultText('No fue posible iniciar la conexión. Inténtalo nuevamente o contacta a soporte.');
        return null;
      }
      const received = payload?.onboarding;
      const session = {
        session_id: received?.session_id,
        state: received?.state,
        expires_at: received?.expires_at,
        client_name: received?.client?.client_name,
      };
      if (!payload?.ok || !isValidOnboardingSession(session) || !saveOnboardingSession(session)) {
        setResultText('No fue posible iniciar la conexión. Inténtalo nuevamente o contacta a soporte.');
        return null;
      }
      setOnboardingSession(session);
      setInvite('');
      removeInviteFromUrl();
      return session;
    } catch (error) {
      setResultText('No fue posible comunicarse con el servicio de conexión. Inténtalo nuevamente.');
      return null;
    }
  };

  const openMetaSignup = (validSession) => {
    if (!sdkReady || typeof window.FB === 'undefined') {
      setLoginStatus(FLOW_STATUS.sdkNotReady);
      setResultText('The Meta SDK is not ready yet. Please try again in a few seconds.');
      return Promise.resolve();
    }

    setOperation('opening');
    setLoginStatus(FLOW_STATUS.openingPopup);
    setResultText('Opening Meta popup to start onboarding...');

    if (window.fbq) {
      window.fbq('trackCustom', 'WhatsAppOnboardingStart', {
        appId: META_APP_ID,
        feature: 'whatsapp_embedded_signup_coexistence',
      });
    }

    const coexistenceExtras = {
      featureType: 'whatsapp_business_app_onboarding',
      sessionInfoVersion: 3,
    };

    const processLoginResponse = async (response) => {
      if (response?.authResponse?.code) {
        if (codeSubmissionLock.current || sessionCompleted.current) return;
        codeSubmissionLock.current = true;
        let metaCode = response.authResponse.code;
        try {
          await postCodeToOnboarding({
            code: metaCode,
            session: validSession,
            setLoginStatus,
            setResultText,
            onSessionConsumed: consumeLocalSession,
          });
        } finally {
          metaCode = '';
          codeSubmissionLock.current = false;
        }
        return;
      }

      if (response?.status === 'not_authorized' || response?.status === 'unknown') {
        setLoginStatus(FLOW_STATUS.cancelled);
        setResultText('Cancelled: the user cancelled or did not authorize the flow.');
        return;
      }

      if (response?.error) {
        setLoginStatus(FLOW_STATUS.backendError);
        setResultText('Could not complete the flow in Meta. Check permissions and try again.');
        return;
      }

      setLoginStatus(FLOW_STATUS.unexpected);
      setResultText('Unexpected popup response. Please try again.');
    };

    return new Promise((resolve) => {
      window.FB.login(
        (response) => { void processLoginResponse(response).finally(resolve); },
        {
          config_id: META_CONFIG_ID,
          response_type: 'code',
          override_default_response_type: true,
          extras: coexistenceExtras,
        }
      );
    });
  };

  const handleStartMetaConnection = async () => {
    if (operationLock.current) return;
    operationLock.current = true;
    try {
      const session = await getOrCreateOnboardingSession();
      if (session) await openMetaSignup(session);
    } finally {
      operationLock.current = false;
      setOperation('idle');
    }
  };

  const canStart = !sessionCompleted.current && sdkReady && missingConfig.length === 0 && hasBackendConfig
    && Boolean(invite || isValidOnboardingSession(onboardingSession)) && operation === 'idle';

  if (invitationRejected) return <NoInvitationView />;

  return (
    <main className="portal-shell">
      <section className="card">
        <h1>WhatsApp Business Connection Portal for Meta</h1>
        <p>
          AmazingMacros provides this portal to connect WhatsApp Business accounts with Meta in a secure and
          controlled way.
        </p>
        <p>
          This portal starts Meta&apos;s official authorization/onboarding process (Embedded Signup) to link the
          account and register onboarding in our systems.
        </p>

        <div className="result-box">
          <h2>Reviewer Information</h2>
          <ul>
            <li><strong>Purpose:</strong> start and validate the connection flow between WhatsApp Business and Meta.</li>
            <li><strong>Main action:</strong> use the “Iniciar conexión con Meta” button.</li>
            <li><strong>Service provider:</strong> AmazingMacros.</li>
            <li><strong>Support:</strong> <a href="mailto:sentryx@amazingmacros.com">sentryx@amazingmacros.com</a></li>
          </ul>
        </div>

        <p><strong>SDK Status:</strong> {sdkStatus}</p>
        <p><strong>Login/Signup Status:</strong> {loginStatus}</p>

        {onboardingSession ? <p><strong>Cliente:</strong> {onboardingSession.client_name}</p> : null}
        {!invite && !onboardingSession ? <p className="note-text">Se requiere una invitación vigente para iniciar la conexión.</p> : null}
        {invite && !onboardingSession ? <p className="note-text">Invitación disponible.</p> : null}

        <button onClick={() => void handleStartMetaConnection()} id="connect-with-facebook" disabled={!canStart}>
          {operation === 'consuming' ? 'Validando invitación...'
            : operation === 'opening' ? 'Abriendo conexión con Meta...'
              : 'Iniciar conexión con Meta'}
        </button>

        <p>
          <a href={PRIVACY_POLICY_PATH}>Privacy Policy</a> ·{' '}
          <a href={DATA_DELETION_PATH}>Data Deletion</a>
        </p>
        <p className="note-text">
          Demo preview for App Review (pre-publication records):{' '}
          <a href={REVIEW_MESSAGES_PATH}>/review/messages</a>
        </p>
        <p><strong>Support:</strong> <a href="mailto:sentryx@amazingmacros.com">sentryx@amazingmacros.com</a></p>

        <div className="result-box">
          <h2>Result</h2>
          <pre>{resultText}</pre>
        </div>
      </section>
    </main>
  );
}

function MessagesReviewView() {
  const TEMPLATE_NAME = 'hello_world';
  const TEMPLATE_LANGUAGE_CODE = 'en_US';
  const TEMPLATE_DEFAULT_PHONE_NUMBER_ID = '1029768290220792';
  const TEMPLATE_DEFAULT_TO = '593987077410';
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState('');
  const [sendStatus, setSendStatus] = useState('Waiting for test send.');
  const [sending, setSending] = useState(false);
  const [templateSendStatus, setTemplateSendStatus] = useState('Waiting for template test send.');
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [formData, setFormData] = useState({
    phone_number_id: '',
    to: '',
    text: '',
  });
  const [templateFormData, setTemplateFormData] = useState({
    phone_number_id: TEMPLATE_DEFAULT_PHONE_NUMBER_ID,
    to: TEMPLATE_DEFAULT_TO,
  });
  const hasMessagingConfig = Boolean(MESSAGING_API_URL && MESSAGING_API_KEY);

  const loadMessages = async () => {
    if (!hasMessagingConfig) {
      setMessagesError('Missing REACT_APP_MESSAGING_API_URL or REACT_APP_MESSAGING_API_KEY variables.');
      setMessages([]);
      return;
    }

    setLoadingMessages(true);
    setMessagesError('');

    try {
      const response = await fetch(`${MESSAGING_API_URL}/api/messages`, {
        method: 'GET',
        headers: {
          'x-messaging-key': MESSAGING_API_KEY,
        },
      });

      const payload = await response.json();
      if (!response.ok) {
        setMessages([]);
        setMessagesError(`Could not load messages (status ${response.status}).`);
        return;
      }

      const records = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : [];
      setMessages(records);
      setMessagesError('');
    } catch (error) {
      setMessages([]);
      setMessagesError('Could not load messages due to a network or service issue.');
      console.error('Error loading review messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    void loadMessages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleTemplateChange = (event) => {
    const { name, value } = event.target;
    setTemplateFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSend = async (event) => {
    event.preventDefault();

    if (!hasMessagingConfig) {
      setSendStatus('Missing REACT_APP_MESSAGING_API_URL or REACT_APP_MESSAGING_API_KEY variables.');
      return;
    }

    setSending(true);
    setSendStatus('Sending test message...');

    try {
      const response = await fetch(`${MESSAGING_API_URL}/api/messages/send-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-messaging-key': MESSAGING_API_KEY,
        },
        body: JSON.stringify(formData),
      });

      const payload = await response.json();
      const workerStatus = payload?.status || response.status;
      const workerError = payload?.result?.error || payload?.error || payload?.message;
      const workerErrorText = typeof workerError === 'string' ? workerError : workerError ? JSON.stringify(workerError) : '';
      const messageId = payload?.result?.meta_message_id || 'n/a';

      if (!response.ok || payload?.ok === false) {
        setSendStatus(
          `Error status ${workerStatus}: ${workerErrorText || 'Could not send the message.'}`
        );
        return;
      }

      setSendStatus(
        `ok: ${String(Boolean(payload?.ok))} | status: ${workerStatus} | meta_message_id: ${messageId}`
      );
      await loadMessages();
    } catch (error) {
      setSendStatus('Could not send the message due to a network or service issue.');
      console.error('Error sending test message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleTemplateSend = async (event) => {
    event.preventDefault();

    if (!hasMessagingConfig) {
      setTemplateSendStatus('Missing REACT_APP_MESSAGING_API_URL or REACT_APP_MESSAGING_API_KEY variables.');
      return;
    }

    setSendingTemplate(true);
    setTemplateSendStatus('Sending template test message...');

    try {
      const response = await fetch(`${MESSAGING_API_URL}/api/messages/send-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-messaging-key': MESSAGING_API_KEY,
        },
        body: JSON.stringify({
          phone_number_id: templateFormData.phone_number_id,
          to: templateFormData.to,
          template_name: TEMPLATE_NAME,
          language_code: TEMPLATE_LANGUAGE_CODE,
        }),
      });

      const payload = await response.json();
      const workerStatus = payload?.status || response.status;
      const workerError = payload?.result?.error || payload?.error || payload?.message;
      const workerErrorText = typeof workerError === 'string' ? workerError : workerError ? JSON.stringify(workerError) : '';
      const messageId = payload?.result?.meta_message_id || 'n/a';

      if (!response.ok || payload?.ok === false) {
        setTemplateSendStatus(
          `Error status ${workerStatus}: ${workerErrorText || 'Could not send the template message.'}`
        );
        return;
      }

      setTemplateSendStatus(
        `ok: ${String(Boolean(payload?.ok))} | status: ${workerStatus} | meta_message_id: ${messageId}`
      );
      await loadMessages();
    } catch (error) {
      setTemplateSendStatus('Could not send the template message due to a network or service issue.');
      console.error('Error sending template test message:', error);
    } finally {
      setSendingTemplate(false);
    }
  };

  return (
    <main className="portal-shell">
      <section className="card review-card">
        <h1>WhatsApp Messages Review</h1>
        <p>
          This page allows reviewers to inspect recent messaging events and run a controlled test send operation
          using the messaging worker.
        </p>
        <p className="note-text">
          Configured endpoint: {MESSAGING_API_URL || 'not set in REACT_APP_MESSAGING_API_URL'}.
        </p>

        <button onClick={() => void loadMessages()} disabled={loadingMessages || !hasMessagingConfig}>
          {loadingMessages ? 'Reloading...' : 'Reload messages'}
        </button>

        {messagesError ? <p className="error-text">{messagesError}</p> : null}

        <div className="result-box table-wrapper">
          <h2>Recent Events</h2>
          {messages.length === 0 ? (
            <p className="note-text">No events to display.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>created_at</th>
                  <th>direction</th>
                  <th>event_type</th>
                  <th>phone_number_id</th>
                  <th>from_wa_id</th>
                  <th>to_wa_id</th>
                  <th>text_body</th>
                  <th>notes</th>
                  <th>meta_status</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((item, index) => (
                  <tr key={`${item?.id || 'message'}-${index}`}>
                    <td>{item?.created_at || '-'}</td>
                    <td>{item?.direction || '-'}</td>
                    <td>{item?.event_type || '-'}</td>
                    <td>{item?.phone_number_id || '-'}</td>
                    <td>{item?.from_wa_id || '-'}</td>
                    <td>{item?.to_wa_id || '-'}</td>
                    <td>{item?.text_body || '-'}</td>
                    <td>{item?.notes || '-'}</td>
                    <td>{item?.meta_status || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="result-box">
          <h2>Send Test Message</h2>
          <form className="send-form" onSubmit={handleSend}>
            <label htmlFor="phone_number_id">phone_number_id</label>
            <input
              id="phone_number_id"
              name="phone_number_id"
              value={formData.phone_number_id}
              onChange={handleChange}
              required
            />

            <label htmlFor="to">to</label>
            <input id="to" name="to" value={formData.to} onChange={handleChange} required />

            <label htmlFor="text">text</label>
            <textarea id="text" name="text" value={formData.text} onChange={handleChange} rows="3" required />

            <button type="submit" disabled={sending || !hasMessagingConfig}>
              {sending ? 'Sending...' : 'Send test message'}
            </button>
          </form>
          <pre>{sendStatus}</pre>
        </div>

        <div className="result-box">
          <h2>Send Template Test</h2>
          <form className="send-form" onSubmit={handleTemplateSend}>
            <label htmlFor="template_phone_number_id">phone_number_id</label>
            <input
              id="template_phone_number_id"
              name="phone_number_id"
              value={templateFormData.phone_number_id}
              onChange={handleTemplateChange}
              required
            />

            <label htmlFor="template_to">to</label>
            <input
              id="template_to"
              name="to"
              value={templateFormData.to}
              onChange={handleTemplateChange}
              required
            />

            <button type="submit" disabled={sendingTemplate || !hasMessagingConfig}>
              {sendingTemplate ? 'Sending...' : 'Send Template Test'}
            </button>
          </form>

          <p className="note-text">
            template_name: <strong>{TEMPLATE_NAME}</strong> · language_code:{' '}
            <strong>{TEMPLATE_LANGUAGE_CODE}</strong>
          </p>

          <h3>Template Send Status</h3>
          <pre>{templateSendStatus}</pre>
        </div>
      </section>
    </main>
  );
}

function App() {
  const currentPath = window.location.pathname;

  if (CALLBACK_PATHS.includes(currentPath)) {
    return <CallbackView />;
  }

  if (currentPath === PRIVACY_POLICY_PATH) {
    return <PrivacyPolicyView />;
  }

  if (currentPath === DATA_DELETION_PATH) {
    return <DataDeletionView />;
  }

  if (currentPath === REVIEW_MESSAGES_PATH) {
    return <MessagesReviewView />;
  }

  if (currentPath === '/' || currentPath === '/onboarding' || currentPath === '/onboarding/') {
    return <OnboardingGate />;
  }

  return (
    <main className="portal-shell">
      <section className="card">
        <h1>Unsupported Route</h1>
        <p>This SPA supports the following routes:</p>
        <ul>
          <li>/</li>
          <li>/auth/facebook/callback</li>
          <li>/oauth/callback</li>
          <li>{PRIVACY_POLICY_PATH}</li>
          <li>{DATA_DELETION_PATH}</li>
          <li>{REVIEW_MESSAGES_PATH}</li>
        </ul>
      </section>
    </main>
  );
}

export default App;
